import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Home, TrendingUp, Users, School, Trees, Phone } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import SEO from '@/components/SEO';
import StructuredData, { schemas } from '@/components/SEO/StructuredData';

// City data configuration
const cityData = {
  'memphis-tn': {
    name: 'Memphis',
    state: 'TN',
    stateFull: 'Tennessee',
    heroImage: 'https://images.unsplash.com/photo-1570698473651-b2de99bae12f?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Memphis is a vibrant city known for its rich musical heritage, world-class BBQ, and growing real estate market. From historic neighborhoods to modern developments, Memphis offers diverse housing options for every lifestyle.',
    highlights: [
      'Home to Beale Street and rich blues history',
      'Affordable cost of living compared to national average',
      'Growing job market with FedEx headquarters',
      'Excellent healthcare with St. Jude and Methodist hospitals',
      'Diverse neighborhoods from Downtown to East Memphis'
    ],
    neighborhoods: ['Downtown', 'Midtown', 'East Memphis', 'Germantown', 'Cooper-Young', 'Harbor Town'],
    avgPrice: '$250,000 - $500,000',
    marketTrend: 'Growing steadily with strong buyer demand'
  },
  'southaven-ms': {
    name: 'Southaven',
    state: 'MS',
    stateFull: 'Mississippi',
    heroImage: 'https://images.unsplash.com/photo-1564013799919-ab600027ffc6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Southaven is the third-largest city in Mississippi and one of the fastest-growing suburbs in the Memphis metro area. Known for excellent schools, family-friendly communities, and affordable housing.',
    highlights: [
      'Top-rated DeSoto County School District',
      'Lower property taxes than Tennessee',
      'Easy commute to Memphis (15-20 minutes)',
      'Snowden Grove Park & BankPlus Amphitheater',
      'Growing retail and dining scene'
    ],
    neighborhoods: ['Snowden Grove', 'Silo Square', 'Plum Point', 'Windwood', 'Rasco Road'],
    avgPrice: '$200,000 - $400,000',
    marketTrend: 'High demand with new construction booming'
  },
  'olive-branch-ms': {
    name: 'Olive Branch',
    state: 'MS',
    stateFull: 'Mississippi',
    heroImage: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Olive Branch consistently ranks among the best places to live in Mississippi. This thriving suburb offers excellent schools, beautiful neighborhoods, and a strong sense of community.',
    highlights: [
      'Named one of the best suburbs in Mississippi',
      'Excellent DeSoto County schools',
      'Cherokee Valley Golf Club',
      'Old Towne Olive Branch historic district',
      'Strong property value appreciation'
    ],
    neighborhoods: ['Whispering Woods', 'Plantation Hills', 'Cherokee Valley', 'Bridgewater', 'Notting Hill'],
    avgPrice: '$250,000 - $450,000',
    marketTrend: 'Consistently appreciating with luxury developments'
  },
  'hernando-ms': {
    name: 'Hernando',
    state: 'MS',
    stateFull: 'Mississippi',
    heroImage: 'https://images.unsplash.com/photo-1605276374104-dee2a0ed3cd6?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Hernando is the county seat of DeSoto County and offers charming small-town living with modern amenities. Known for its historic courthouse square, excellent schools, and family-oriented atmosphere.',
    highlights: [
      'Charming historic downtown square',
      'DeSoto County seat with strong community',
      'Arkabutla Lake nearby for recreation',
      'Lower cost of living with quality homes',
      'Growing with new subdivisions'
    ],
    neighborhoods: ['Downtown Hernando', 'Silo Square', 'Cedar Hill', 'Wynbrooke', 'The Preserve'],
    avgPrice: '$200,000 - $380,000',
    marketTrend: 'Steady growth with affordable options'
  },
  'collierville-tn': {
    name: 'Collierville',
    state: 'TN',
    stateFull: 'Tennessee',
    heroImage: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Collierville is one of the most desirable suburbs in the Memphis area, known for its historic Town Square, top-rated schools, and upscale living. This charming town offers a perfect blend of Southern hospitality and modern convenience.',
    highlights: [
      'Historic Town Square with shops & restaurants',
      'Top-rated Collierville Schools',
      'Low crime rate and family-friendly',
      'Beautiful parks and greenways',
      'Upscale shopping and dining'
    ],
    neighborhoods: ['Historic District', 'Schilling Farms', 'Woodlands', 'Forest Hill', 'Carriage Hills'],
    avgPrice: '$350,000 - $700,000',
    marketTrend: 'Premium market with strong demand'
  },
  'germantown-tn': {
    name: 'Germantown',
    state: 'TN',
    stateFull: 'Tennessee',
    heroImage: 'https://images.unsplash.com/photo-1600047509807-ba8f99d2cdde?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    description: 'Germantown is an affluent suburb known for exceptional schools, beautiful tree-lined streets, and a high quality of life. This prestigious community offers luxury homes, excellent amenities, and a strong sense of community.',
    highlights: [
      'Award-winning Germantown Municipal Schools',
      'Germantown Performing Arts Center',
      'Wolf River Greenway trails',
      'Saddle Creek Shopping Center',
      'Beautiful established neighborhoods'
    ],
    neighborhoods: ['Riverdale', 'Forest Hill-Irene', 'Dogwood Park', 'Farmington', 'Houston Levee'],
    avgPrice: '$400,000 - $800,000+',
    marketTrend: 'Luxury market with consistent appreciation'
  }
};

const CityLandingPage = ({ citySlug }) => {
  const city = cityData[citySlug];
  
  if (!city) {
    return <div>City not found</div>;
  }

  return (
    <div className="min-h-screen">
      <SEO 
        title={`Homes for Sale in ${city.name}, ${city.state} | Real Estate & Property`}
        description={`Find your dream home in ${city.name}, ${city.stateFull}. Bespoke Realty is a Black-owned, woman-led brokerage offering personalized real estate services. Browse homes for sale today!`}
        keywords={`${city.name} homes for sale, ${city.name} ${city.state} real estate, houses for sale ${city.name}, ${city.name} property, buy home ${city.name} ${city.stateFull}`}
      />
      <StructuredData schema={[
        schemas.cityPlace(city),
        schemas.localBusiness({ city: city.name }),
        schemas.breadcrumb([
          { name: 'Home', url: '/' },
          { name: `${city.name} Real Estate`, url: `/${citySlug}-real-estate` },
        ]),
      ]} />

      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${city.heroImage})` }}
        ></div>
        <div className="relative z-20 text-center text-white px-4 max-w-4xl mx-auto">
          <div className="flex items-center justify-center gap-2 mb-4">
            <MapPin className="h-6 w-6 text-[#BCA480]" />
            <span className="text-lg text-white/90">{city.stateFull}</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            {city.name} Real Estate
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Find your perfect home in {city.name}, {city.state}
          </p>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 bg-[#BCA480]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-white text-center">
            <div>
              <Home className="h-8 w-8 mx-auto mb-2" />
              <p className="font-semibold">Avg. Price Range</p>
              <p className="text-sm opacity-90">{city.avgPrice}</p>
            </div>
            <div>
              <TrendingUp className="h-8 w-8 mx-auto mb-2" />
              <p className="font-semibold">Market Trend</p>
              <p className="text-sm opacity-90">{city.marketTrend}</p>
            </div>
            <div>
              <School className="h-8 w-8 mx-auto mb-2" />
              <p className="font-semibold">Great Schools</p>
              <p className="text-sm opacity-90">Top-rated districts</p>
            </div>
            <div>
              <Users className="h-8 w-8 mx-auto mb-2" />
              <p className="font-semibold">Community</p>
              <p className="text-sm opacity-90">Family-friendly</p>
            </div>
          </div>
        </div>
      </section>

      {/* About the City */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-[#434345] mb-6">
                Why Live in {city.name}?
              </h2>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {city.description}
              </p>
              <ul className="space-y-3">
                {city.highlights.map((highlight, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <span className="text-[#BCA480] mt-1">✓</span>
                    <span className="text-gray-700">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Card className="bg-gradient-to-br from-[#BCA480]/10 to-white">
                <CardContent className="p-6 text-center">
                  <Trees className="h-10 w-10 mx-auto mb-3 text-[#BCA480]" />
                  <h3 className="font-semibold text-[#434345]">Quality of Life</h3>
                  <p className="text-sm text-gray-600 mt-2">Beautiful neighborhoods & amenities</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#434345]/10 to-white">
                <CardContent className="p-6 text-center">
                  <TrendingUp className="h-10 w-10 mx-auto mb-3 text-[#434345]" />
                  <h3 className="font-semibold text-[#434345]">Investment Value</h3>
                  <p className="text-sm text-gray-600 mt-2">Strong appreciation potential</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#434345]/10 to-white">
                <CardContent className="p-6 text-center">
                  <School className="h-10 w-10 mx-auto mb-3 text-[#434345]" />
                  <h3 className="font-semibold text-[#434345]">Education</h3>
                  <p className="text-sm text-gray-600 mt-2">Excellent school options</p>
                </CardContent>
              </Card>
              <Card className="bg-gradient-to-br from-[#BCA480]/10 to-white">
                <CardContent className="p-6 text-center">
                  <Users className="h-10 w-10 mx-auto mb-3 text-[#BCA480]" />
                  <h3 className="font-semibold text-[#434345]">Community</h3>
                  <p className="text-sm text-gray-600 mt-2">Welcoming neighborhoods</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Neighborhoods */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#434345] text-center mb-4">
            Popular Neighborhoods in {city.name}
          </h2>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto">
            Discover the diverse neighborhoods that make {city.name} a great place to call home.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {city.neighborhoods.map((neighborhood, idx) => (
              <div 
                key={idx}
                className="bg-white p-4 rounded-lg text-center border hover:border-[#BCA480] hover:shadow-md transition-all"
              >
                <MapPin className="h-6 w-6 mx-auto mb-2 text-[#BCA480]" />
                <p className="font-medium text-[#434345] text-sm">{neighborhood}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-gradient-to-br from-[#434345] to-[#5a5a5c] p-8 md:p-12 rounded-2xl text-white">
            <h2 className="text-2xl md:text-3xl font-bold mb-4">
              Ready to Find Your Home in {city.name}?
            </h2>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              As a Black-owned, woman-led brokerage, we're committed to providing personalized service 
              and helping you find the perfect home in {city.name}, {city.state}.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-[#BCA480] hover:bg-[#a89470] text-white"
              >
                <Link to="/contact">
                  <Phone className="h-4 w-4 mr-2" />
                  Contact Us Today
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-[#434345]"
              >
                <Link to="/mls">
                  Browse All Listings
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Other Cities */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-xl font-semibold text-[#434345] text-center mb-6">
            Explore Other Areas We Serve
          </h3>
          <div className="flex flex-wrap justify-center gap-3">
            {Object.entries(cityData).map(([slug, data]) => (
              slug !== citySlug && (
                <Link
                  key={slug}
                  to={`/${slug}-real-estate`}
                  className="px-4 py-2 bg-white border rounded-full text-sm text-[#434345] hover:border-[#BCA480] hover:text-[#BCA480] transition-colors"
                >
                  {data.name}, {data.state}
                </Link>
              )
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

// Export individual city components
export const MemphisTN = () => <CityLandingPage citySlug="memphis-tn" />;
export const SouthavenMS = () => <CityLandingPage citySlug="southaven-ms" />;
export const OliveBranchMS = () => <CityLandingPage citySlug="olive-branch-ms" />;
export const HernandoMS = () => <CityLandingPage citySlug="hernando-ms" />;
export const ColliervilleTN = () => <CityLandingPage citySlug="collierville-tn" />;
export const GermantownTN = () => <CityLandingPage citySlug="germantown-tn" />;

export default CityLandingPage;
